// Impersonation is a short-lived, admin-initiated session — a way to look at the app
// through a user's eyes for a few minutes, never a second login. Everything that keeps
// it that way lives here: the localStorage keys, the deadline, and the revert.
//
// Two rules this module exists to enforce:
//   1. The identity keys ('role', 'hasPinCode', 'pinUser', and the 2FA window) must
//      always describe the account whose refresh token sits in localStorage. Restoring
//      the admin's tokens without restoring those is what left the app greeting the
//      admin with the impersonated user's face and asking for a PIN only that user knew.
//   2. An unattended impersonation must not survive. Coming back hours — or a day —
//      later reverts to the original admin instead of resuming as the target user.

// Absolute lifetime, counted from the moment impersonation starts. Impersonation is for
// fast actions; anything longer should be a support call, not a silent session. Kept in
// step with IMPERSONATION_TTL_MS in bac-server/src/routes/auth.js.
export const IMPERSONATION_TTL_MS = 30 * 60 * 1000;

const KEYS = {
    flag: 'isImpersonating',
    startedAt: 'impersonationStartedAt',
    token: 'originalToken',
    refreshToken: 'originalRefreshToken',
    identity: 'originalIdentity',
    targetUser: 'impersonatedUser',
    notice: 'impersonationExpiredNotice',
};

// Everything that must describe one single principal. 'role'/'hasPinCode'/'pinUser'
// drive every PIN gate (api.js, utils/api-request.js, Layout, Login) and the lock
// screen's avatar; the 2FA pair is a global 5-minute pass the backend trusts verbatim,
// so a challenge answered by the impersonated user must never carry back to the admin.
const IDENTITY_KEYS = ['role', 'hasPinCode', 'pinUser', '2fa-verified', '2fa-timestamp'];

// Everything impersonation owns — logout clears the lot.
export const IMPERSONATION_LS_KEYS = Object.values(KEYS);

const readIdentity = () => IDENTITY_KEYS.reduce((identity, key) => {
    identity[key] = localStorage.getItem(key);
    return identity;
}, {});

const writeIdentity = (identity) => {
    IDENTITY_KEYS.forEach(key => {
        const value = identity?.[key];
        if (value === null || value === undefined) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, value);
        }
    });
};

export const isImpersonating = () => !!localStorage.getItem(KEYS.flag);

export const getImpersonatedUserId = () => {
    const userId = localStorage.getItem(KEYS.targetUser);
    return userId && userId !== 'null' && userId !== 'undefined' ? userId : null;
};

export const setImpersonatedUserId = (userId) => localStorage.setItem(KEYS.targetUser, userId);

// Where to land once impersonation ends: the page of the user we were impersonating,
// which is where the admin started from and a route their own role can open.
const exitPathFor = (userId) => (userId ? `/users/${userId}` : '/');

export const clearImpersonation = () => IMPERSONATION_LS_KEYS.forEach(key => localStorage.removeItem(key));

// Stash the admin's session, then switch BOTH the access and the refresh token to the
// impersonated one. Keeping the admin refresh token here would make any mid-impersonation
// refresh mint a new admin access token over the impersonated one, silently dropping us
// out of the session before Stop is pressed.
export const beginImpersonation = ({ token, refreshToken }) => {
    // Only ever stash a real admin session. A second start (a stale tab, a double click)
    // would otherwise overwrite the stashed admin token with an impersonated one, and
    // there would be nothing left to come back to.
    if (!isImpersonating()) {
        localStorage.setItem(KEYS.token, localStorage.getItem('token'));
        localStorage.setItem(KEYS.refreshToken, localStorage.getItem('refreshToken'));
        localStorage.setItem(KEYS.identity, JSON.stringify(readIdentity()));
        // The deadline belongs to the stash, not to the target: starting a second
        // impersonation without stopping the first must not buy another 30 minutes.
        localStorage.setItem(KEYS.startedAt, String(Date.now()));
    }

    localStorage.setItem(KEYS.flag, '1');
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    // The impersonated session starts with no inherited 2FA pass, and leaves none behind.
    localStorage.removeItem('2fa-verified');
    localStorage.removeItem('2fa-timestamp');
};

const expiresAt = () => {
    const startedAt = Number(localStorage.getItem(KEYS.startedAt));
    // No start stamp means a session opened before this feature shipped — treat it as
    // long overdue rather than immortal.
    return Number.isFinite(startedAt) && startedAt > 0 ? startedAt + IMPERSONATION_TTL_MS : 0;
};

export const msRemaining = () => (isImpersonating() ? Math.max(0, expiresAt() - Date.now()) : 0);

export const isExpired = () => isImpersonating() && Date.now() >= expiresAt();

// Puts the admin back: their tokens, their identity keys, no impersonation leftovers.
// Returns what the caller still needs to close the session server-side, since the keys
// are gone by then.
const restoreOriginalSession = () => {
    const impersonatedToken = localStorage.getItem('token');
    const impersonatedUserId = getImpersonatedUserId();
    const originalToken = localStorage.getItem(KEYS.token);
    const originalRefreshToken = localStorage.getItem(KEYS.refreshToken);

    if (originalToken) {
        localStorage.setItem('token', originalToken);
        if (originalRefreshToken) {
            localStorage.setItem('refreshToken', originalRefreshToken);
        } else {
            // The impersonated refresh token is a live credential for someone else's
            // account: never keep it, even when there is nothing to put in its place.
            localStorage.removeItem('refreshToken');
        }

        try {
            writeIdentity(JSON.parse(localStorage.getItem(KEYS.identity)));
        } catch {
            // No usable snapshot (a session from before this shipped): drop the
            // impersonated user's identity rather than leave it speaking for the admin.
            // fetchLoggedUser restamps it from /auth/me once the admin token is back.
            writeIdentity(null);
        }
    } else {
        // The flag was set, so the tokens sitting here are the impersonated ones — and
        // the admin session they were stashed against is gone. There is nothing safe to
        // return to, so drop the credentials and let the admin sign in again.
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        writeIdentity(null);
    }

    clearImpersonation();

    return { impersonatedToken, impersonatedUserId, originalToken };
};

// Best-effort server-side close, deliberately outside the api client: this runs at boot
// before the store exists, and a 401 here must never reach the interceptor's PIN/logout
// flow. Deactivating the session row is what actually revokes the impersonated tokens —
// wiping them from localStorage is only hygiene.
//
// Both handles are used, and in parallel. The impersonated token names THIS session and
// keeps the audit trail linked, but the backend rejects it once the deadline has passed;
// the admin token closes what that admin left behind, which is the only way in from then
// on. Chaining them would mean the second request is never sent — every revert navigates.
const closeServerSession = ({ impersonatedToken, adminToken, impersonatedUserId }) => {
    const attempt = async (token, body) => {
        if (!token) return false;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/stop-impersonate`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                // Every revert is followed by a page load; without this the request that
                // closes the session would be cancelled by the navigation.
                keepalive: true,
            });
            return response.ok;
        } catch {
            return false;
        }
    };

    return Promise.all([
        attempt(impersonatedToken, {}),
        attempt(adminToken, { userId: impersonatedUserId }),
    ]).then(outcomes => outcomes.some(Boolean));
};

let revertInFlight = null;

// True from the moment the local revert lands until the server call settles. During that
// window localStorage already says "not impersonating" while the UI has not caught up —
// watchers must sit still rather than read the gap as another tab having ended it.
export const isReverting = () => !!revertInFlight;

// The one way out of impersonation, shared by the Stop button, the deadline, the tab
// focus check and any 401. `reason` is 'manual' or 'expired'; the local revert is
// unconditional and immediate either way, and the server call that follows is
// best-effort — a network blip must not leave an admin holding an org user's token.
export const revertToAdmin = ({ reason = 'manual' } = {}) => {
    if (revertInFlight) return revertInFlight;
    if (!isImpersonating()) return Promise.resolve({ exitPath: exitPathFor(null), reverted: false });

    const { impersonatedToken, impersonatedUserId, originalToken } = restoreOriginalSession();

    if (reason === 'expired') {
        // Set after the clear above: the notice has to outlive the revert (and the
        // reload that usually follows) to be shown once the UI is mounted.
        localStorage.setItem(KEYS.notice, '1');
    }

    revertInFlight = closeServerSession({ impersonatedToken, adminToken: originalToken, impersonatedUserId })
        .then(serverClosed => {
            if (!serverClosed) {
                // The local session is the admin's either way. The row is left for the
                // server-side deadline to close, which it does on the next use.
                console.warn('Impersonation ended locally; the server session could not be closed.');
            }
            return { exitPath: exitPathFor(impersonatedUserId), reverted: true, serverClosed };
        })
        .finally(() => { revertInFlight = null; });

    return revertInFlight;
};

// Runs once, synchronously, before the auth slice reads localStorage for its initial
// state — so an impersonation that ran out while the tab was closed never becomes the
// session the app boots into.
export const enforceImpersonationTtlOnBoot = () => {
    if (!isExpired()) return false;

    const exitPath = exitPathFor(getImpersonatedUserId());
    revertToAdmin({ reason: 'expired' });

    // The URL was opened as the impersonated user and may well be an organization-only
    // page the admin's own account has no business rendering. Restart the load on their
    // side of the app; the expiry notice survives it and is shown once we get there.
    if (window.location.pathname !== exitPath) {
        window.location.replace(exitPath);
    }

    return true;
};

// One-shot: the expiry notice is shown once, by whoever mounts first.
export const consumeExpiredNotice = () => {
    if (!localStorage.getItem(KEYS.notice)) return false;
    localStorage.removeItem(KEYS.notice);
    return true;
};
